import type { Project } from '@elec/domain';
import { controlInfo, pointKind } from '@elec/analysis';
import { fitsOnA3, isStray, likelyTag } from '@elec/editor';
import type { Finding } from '@elec/rules';
import { quickFix, type QuickFixKind } from './quickFixes.ts';

/**
 * The buttons of a finding besides "Fix with the assistant": where the user enters information
 * only they have (the title block, measurements, a value read on a device), or a one-click fix
 * the editor does itself (numbering, an earthed socket, a room around a point…).
 */
export type FindingGoto =
  /** File → Project information, on the first missing field. */
  | 'projectInfo'
  /** Inspection → Test results. */
  | 'measurements'
  /** The circuit table (cable, length). */
  | 'circuits'
  /** Select the element and open its properties. */
  | 'properties'
  /** Regenerate the out-of-date sheet. */
  | 'update'
  /** Generate the diagrams from the site survey. */
  | 'survey'
  /** Show the sheet and open its settings (paper size). */
  | 'sheet'
  /** Show the elements on the drawing, where the user draws the fix. */
  | 'show'
  /** Add an empty position plan and open it, with its "To place" list. */
  | 'newPlan'
  /** Draw the earth electrode beside the supply and open its properties. */
  | 'addEarth'
  /** Number circuits A, B… and their points. */
  | 'renumber'
  /** Number the terminals of each strip. */
  | 'numberTerminals'
  /** Switch the sheet to A3 (everything fits there). */
  | 'toA3'
  /** Replace the sockets without earth by earthed ones. */
  | 'earthSockets'
  /** Rename the elements to the tag in `arg` (a typo between coil and contacts). */
  | 'rename'
  /** Connect the point to a circuit, chosen in a list. */
  | 'attach'
  /** Draw a room around the point, then choose its type. */
  | 'drawRoom'
  /** Delete the element (drawn by mistake). */
  | 'delete'
  /** Mark the circuit as a spare. */
  | 'spare'
  /** Estimate the missing lengths from the floor plan. */
  | 'estimateLengths'
  /** Read the missing value on a photo of the device or the cable. */
  | 'photo'
  /** Mark the board's blanking plates fitted (R-BOARD-01). */
  | 'blankingOk'
  /** Spread the board's single-phase circuits over the phases (R-CALC-06). */
  | 'balance'
  /** Add a north arrow to the plan and open its properties (R-PLAN-02). */
  | 'addNorth'
  /** Draw the cable routes from the board to the points (R-PLAN-03). */
  | 'drawRoutes'
  /** A one-click fix by the editor (quickFixes.ts): `quick` says which. */
  | 'quick';

export interface FindingButton {
  to: FindingGoto;
  /** rename: the tag the contacts take. */
  arg?: string;
  /** rename: the tag they had. */
  from?: string;
  quick?: QuickFixKind;
}

/** Findings whose fix is information the user enters, or work on the drawing. */
const GOTO: Record<string, FindingGoto[]> = {
  'R-DOC-02': ['projectInfo'],
  'R-TEST-00': ['measurements'],
  'R-TEST-01': ['measurements'],
  'R-TEST-02': ['measurements'],
  'R-TEST-04': ['measurements'],
  'R-CALC-00': ['estimateLengths', 'circuits'],
  'R-CIR-04': ['photo', 'circuits'],
  'R-DEV-01': ['photo', 'properties'],
  'R-CALC-05': ['properties'],
  'R-ML-01': ['update'],
  'R-SRV-01': ['survey'],
  'R-DOC-04': ['toA3', 'sheet'],
  'R-PLAN-01': ['drawRoom', 'show'],
  'R-TOPO-01': ['attach', 'delete', 'show'],
  'R-TOPO-02': ['attach', 'delete', 'show'],
  'R-CTL-01': ['rename', 'show'],
  'R-CTL-02': ['rename', 'show'],
  'R-CIR-07': ['renumber'],
  'R-DOC-01': ['renumber'],
  'R-CTL-03': ['numberTerminals'],
  'R-BOARD-01': ['blankingOk', 'show'],
  'R-CALC-06': ['balance', 'show'],
  'R-PLAN-02': ['addNorth'],
  'R-PLAN-03': ['drawRoutes', 'show'],
};

/** Findings the assistant can fix as well (with its actions), beside their buttons. */
const ALSO_ASSISTANT = new Set(['R-CIR-04', 'R-CALC-05', 'R-CALC-00']);
/** Buttons added to the assistant's for findings it can fix. */
const WITH_ASSISTANT: Record<string, FindingGoto[]> = {
  'R-CIR-05': ['earthSockets'],
  'FR-CIR-05': ['earthSockets'],
  'NL-CIR-02': ['earthSockets'],
  'R-CIR-06': ['spare'],
  'R-TEST-03': ['measurements'],
};

/**
 * The finding's buttons: "Fix with the assistant" when it can act on it, and the others; null
 * for none (an info, or nothing to point at). `project`: to offer only the buttons that would
 * work (a likely tag, a sheet that fits on A3, a floor plan to measure on…).
 */
export function findingAction(
  f: Finding,
  project?: Project,
): { assistant: boolean; buttons: FindingButton[] } | null {
  const a = baseAction(f, project);
  // A one-click fix comes first, beside the assistant (which can do it in other ways).
  const q = project && f.severity !== 'info' ? quickFix(project, f) : null;
  if (!q) return a;
  const button: FindingButton = { to: 'quick', arg: q.arg, quick: q.to };
  return a ? { ...a, buttons: [button, ...a.buttons] } : { assistant: true, buttons: [button] };
}

function baseAction(f: Finding, project?: Project): { assistant: boolean; buttons: FindingButton[] } | null {
  const located = f.targets.length > 0 || !!f.sheetId;
  // The position plan: the assistant drafts it or adds the missing points; the user can also
  // start one, or see the points that are missing.
  if (f.ruleId === 'R-DOC-03') return { assistant: true, buttons: [{ to: located ? 'show' : 'newPlan' }] };
  // The earth electrode: the assistant draws it or sets the value the user gives; the user can
  // also draw it in one click, or enter the measured value with the other test results.
  if (/EARTH-01$/.test(f.ruleId))
    return { assistant: true, buttons: [{ to: f.targets.length ? 'measurements' : 'addEarth' }] };
  // An info (lengths to measure, a point outside any room, tests to do) gets its buttons, not the assistant.
  if (f.severity === 'info' && !GOTO[f.ruleId]) return null;
  if (WITH_ASSISTANT[f.ruleId])
    return { assistant: true, buttons: WITH_ASSISTANT[f.ruleId]!.map((to) => ({ to })) };
  const list = GOTO[f.ruleId];
  if (!list) return { assistant: true, buttons: [] };
  const buttons = list.flatMap((to): FindingButton[] => {
    if (to === 'properties' && !f.targets.length) return [];
    if ((to === 'show' || to === 'update' || to === 'sheet') && !located) return [];
    if (!project) return to === 'rename' || to === 'toA3' || to === 'estimateLengths' ? [] : [{ to }];
    if (to === 'toA3') return f.sheetId && fitsOnA3(project, f.sheetId) ? [{ to }] : [];
    // Routes need the board on the plan (the finding then points at it).
    if (to === 'drawRoutes') return f.targets.length ? [{ to }] : [];
    if (to === 'balance') return f.sheetId ? [{ to }] : [];
    if (to === 'attach') {
      const e = project.elements[f.targets[0] ?? ''];
      return e && pointKind(e) && project.sheets[e.sheetId]?.kind === 'single-line' ? [{ to }] : [];
    }
    if (to === 'delete') return f.targets.length === 1 && isStray(project, f.targets[0]!) ? [{ to }] : [];
    if (to === 'rename') {
      // R-CTL-01: these contacts take the coil's tag. R-CTL-02: the contacts of the near tag
      // take this coil's.
      const e = project.elements[f.targets[0] ?? ''];
      if (!e) return [];
      const coil = f.ruleId === 'R-CTL-02';
      const tag = likelyTag(project, e.tag, coil ? 'contact' : 'coil');
      if (!tag) return [];
      return [coil ? { to, from: tag, arg: e.tag } : { to, from: e.tag, arg: tag }];
    }
    if (to === 'estimateLengths')
      return Object.values(project.sheets).some(
        (s) =>
          s.kind === 'position-plan' &&
          s.background &&
          Object.values(project.elements).some((e) => e.sheetId === s.id && e.symbolId === 'iec.board'),
      )
        ? [{ to }]
        : [];
    return [{ to }];
  });
  return { assistant: ALSO_ASSISTANT.has(f.ruleId) && f.severity !== 'info', buttons };
}

/** The contacts a rename button retags: those of the finding (R-CTL-01) or of the near tag (R-CTL-02). */
export function renameTargets(project: Project, f: Finding, b: FindingButton): string[] {
  if (f.ruleId !== 'R-CTL-02') return f.targets;
  const r = [...controlInfo(project).relays.values()].find((x) => x.tag === b.from);
  return r ? r.contacts.map((c) => c.id) : [];
}
